import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import { AlertTriangle, Home, RefreshCcw } from 'lucide-react'
import { isRouteErrorResponse, useRouteError } from 'react-router'

import { Button } from '@fortem-monorepo/react-ui'
import { getErrorDescription, getErrorMessage } from '@fortem-monorepo/web-sdk'

type ApplicationErrorPageProps = {
  error?: unknown
  resetError?: () => void
}

const getRouteErrorMessage = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return error.statusText || `${error.status} error`
  }

  return getErrorMessage(error, 'Sorry, something went wrong.')
}

const getRouteErrorDescription = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return error.data && typeof error.data === 'string' ? error.data : undefined
  }

  return getErrorDescription(error)
}

export const ApplicationErrorPage: React.FC<ApplicationErrorPageProps> = ({
  error,
  resetError,
}) => {
  const message = getRouteErrorMessage(error)
  const description = getRouteErrorDescription(error)

  return (
    <main
      role="alert"
      className="min-h-screen bg-background text-foreground flex items-center justify-center p-6"
    >
      <section className="w-full max-w-xl border bg-white p-6 shadow-sm rounded-md">
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
            <AlertTriangle aria-hidden="true" className="size-5" />
          </div>
          <div className="min-w-0 space-y-3">
            <div>
              <h1 className="text-lg font-semibold text-heading">Something went wrong</h1>
              <p className="mt-1 text-sm text-muted-foreground">{message}</p>
              {description && <p className="mt-2 text-xs text-muted-foreground">{description}</p>}
            </div>

            <p className="text-sm text-muted-foreground">
              The team has been notified. You can try again, refresh the page, or return to the
              dashboard.
            </p>

            <div className="flex flex-wrap gap-2">
              {resetError && (
                <Button type="button" variant="outline" onClick={resetError}>
                  <RefreshCcw aria-hidden="true" />
                  Try again
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => window.location.reload()}>
                <RefreshCcw aria-hidden="true" />
                Refresh
              </Button>
              <Button type="button" onClick={() => window.location.assign('/')}>
                <Home aria-hidden="true" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export const RouteErrorPage = () => {
  const error = useRouteError()

  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return <ApplicationErrorPage error={error} />
}
